import { useEffect, useState } from "react";

import { swSettings } from "app/common/services/swSettings";
import type { CompetitorSchema } from "app/pages/activation/components/SetupModal/CompetitorsStep/types";
import type { PrimaryWebsite, SetupModalSchema } from "app/pages/activation/components/SetupModal/types";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { activationTracking } from "app/pages/activation/services/tracking";

type UseSetupCompetitorsSchema = {
    selectedPrimaryEntity: SetupModalSchema["selectedPrimaryEntity"];
    isOpen: SetupModalSchema["isOpen"];
};

export const useSetupCompetitors = ({ selectedPrimaryEntity, isOpen }: UseSetupCompetitorsSchema) => {
    const [primary, setPrimary] = useState<PrimaryWebsite | null>(null);
    const [savedThreadId, setSavedThreadId] = useState("");
    const [selectedCompetitors, setSelectedCompetitors] = useState<CompetitorSchema[]>([]);

    const track = activationTracking.setup;

    const defaultCountry = swSettings?.current?.defaultParams?.country;

    const [selectedCountry, setSelectedCountry] = useState<number>();

    const onCompetitorSelect = (competitor: CompetitorSchema) => {
        const isCompetitorSelected = selectedCompetitors.find(
            (selectedCompetitor) => selectedCompetitor.name === competitor.name,
        );

        if (!isCompetitorSelected) {
            track.trackAddCompetitorClick(competitor.name, selectedCompetitors.length + 1);
            setSelectedCompetitors([...selectedCompetitors, competitor]);
        } else {
            track.trackRemoveCompetitorClick(competitor.name, selectedCompetitors.length - 1);
            const filterCompetitors = selectedCompetitors.filter(
                (selectedCompetitor) => selectedCompetitor.name !== competitor.name,
            );

            setSelectedCompetitors(filterCompetitors);
        }
    };

    const onCountrySelect = (countryCode: number) => {
        setSelectedCountry(countryCode);
    };

    useEffect(() => {
        setPrimary(selectedPrimaryEntity);
    }, [selectedPrimaryEntity?.name]);

    useEffect(() => {
        const isMultiCountry = ActivationComponentAssets.isMultiCountries;

        if (!isMultiCountry && !selectedCountry) {
            setSelectedCountry(defaultCountry);
        }

        if (!isOpen && !selectedCountry) {
            setSelectedCountry(undefined);

            return;
        }
    }, [isOpen]);

    return {
        primary,
        setPrimary,
        savedThreadId,
        setSavedThreadId,
        selectedCompetitors,
        setSelectedCompetitors,
        onCompetitorSelect,
        selectedCountry,
        onCountrySelect,
    };
};
